import { useState } from "react";
import { Alert, Drawer, ErrorState, KeyValues, LoadingState } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useIntegrationLogQuery } from "../services/queries/useIntegrationLogQuery";
import {
  EMPTY_CELL,
  formatDurationMs,
  formatJsonPayload,
  formatLogTimestamp,
} from "../services/integration-log-format";
import {
  IntegrationLogDirectionChip,
  IntegrationLogStatusChip,
} from "./IntegrationLogChips";
import { IntegrationLogReference } from "./IntegrationLogReference";
import type { IntegrationLogDetailDrawerProps } from "../types/integration-logs.types";

const mono = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
} as const;

const sectionHeading = {
  margin: "4px 0 0",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--rd-text-meta)",
} as const;

type PayloadBlockProps = {
  title: string;
  emptyLabel: string;
  payload: string | null;
};

function PayloadBlock({ title, emptyLabel, payload }: PayloadBlockProps) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h3 style={sectionHeading}>{title}</h3>
      {payload === null ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-tertiary)" }}>{emptyLabel}</p>
      ) : (
        <pre
          dir="ltr"
          style={{
            ...mono,
            margin: 0,
            maxHeight: 320,
            overflow: "auto",
            padding: "12px 14px",
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-surface-soft)",
            boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            textAlign: "start",
            color: "var(--rd-text)",
          }}
        >
          {payload}
        </pre>
      )}
    </section>
  );
}

/**
 * Row-click detail view for the integration log. Fetches
 * `GET /admin/integration-logs/:id` (list row + request/response payloads) and
 * renders the payloads verbatim — they arrive already redacted server-side.
 */
export function IntegrationLogDetailDrawer({
  locale,
  logId,
  onClose,
}: IntegrationLogDetailDrawerProps) {
  const t = useTranslations();
  const uiLocale = useLocale();
  const page = t(keys.integrationLogs.detailTitle).toLocaleLowerCase(uiLocale);
  // Keep the most recently selected query binding while closed. Clearing the
  // parent's selection must not replace its detail body before the next row.
  const [lastLogId, setLastLogId] = useState<string | null>(logId);
  if (logId && logId !== lastLogId) {
    setLastLogId(logId);
  }

  const { data, isLoading, error, refetch } = useIntegrationLogQuery(
    logId ?? lastLogId,
  );

  const suffix = t(keys.integrationLogs.durationSuffix);

  return (
    <Drawer
      open={Boolean(logId)}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      crumb={`${t(keys.integrationLogs.title)} / ${t(keys.integrationLogs.detailTitle)}`}
      badge="hub"
      width={520}
      title={
        data
          ? `${data.provider}${data.operation ? ` · ${data.operation}` : ""}`
          : t(keys.integrationLogs.detailTitle)
      }
      // Provider and id are caller DATA in a box wider than the line, so
      // `<bdi>` rather than `dir="auto"` — the latter would re-align the
      // whole row and shove a Latin id against the far edge in Arabic. The
      // sub line stays rendered while the drawer is closed, so the last
      // selected row is still the one on screen as it slides out.
      sub={data ? <bdi>{`${data.provider} · ${data.id}`}</bdi> : undefined}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {isLoading ? (
          <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} rows={0} />
        ) : null}

        {!isLoading && (error || !data) ? (
          <ErrorState
            title={t(keys.shell.errorTitle, { page })}
            sub={t(keys.shell.errorSub)}
            onRetry={() => void refetch()}
            retryLabel={t(keys.shell.retry)}
          />
        ) : null}

        {!isLoading && data ? (
          <>
            <section aria-label={t(keys.integrationLogs.detailSummaryTitle)}>
              <KeyValues
                rows={[
                  {
                    label: t(keys.integrationLogs.columnTime),
                    value: <span className="rd-num">{formatLogTimestamp(data.occurredAt, locale)}</span>,
                  },
                  {
                    label: t(keys.integrationLogs.columnStatus),
                    value: <IntegrationLogStatusChip status={data.status} />,
                  },
                  {
                    label: t(keys.integrationLogs.columnDirection),
                    value: <IntegrationLogDirectionChip direction={data.direction} />,
                  },
                  { label: t(keys.integrationLogs.columnProvider), value: data.provider },
                  {
                    label: t(keys.integrationLogs.columnOperation),
                    value: <span dir="ltr" style={mono}>{data.operation || EMPTY_CELL}</span>,
                  },
                  { label: t(keys.integrationLogs.columnStore), value: data.storeName || EMPTY_CELL },
                  {
                    label: t(keys.integrationLogs.columnReference),
                    value: (
                      <IntegrationLogReference
                        locale={locale}
                        reference={data.reference}
                        entityType={data.entityType}
                        entityId={data.entityId}
                        storeId={data.storeId}
                      />
                    ),
                  },
                  { label: t(keys.integrationLogs.detailEntityType), value: data.entityType || EMPTY_CELL },
                  {
                    label: t(keys.integrationLogs.detailMethod),
                    value: <span dir="ltr" style={mono}>{data.method || EMPTY_CELL}</span>,
                  },
                  {
                    label: t(keys.integrationLogs.detailUrl),
                    value: (
                      <span dir="ltr" style={{ ...mono, overflowWrap: "anywhere" }}>
                        {data.url || EMPTY_CELL}
                      </span>
                    ),
                  },
                  {
                    label: t(keys.integrationLogs.columnHttpStatus),
                    value: (
                      <span className="rd-num" style={data.httpStatus !== null && data.httpStatus >= 400 ? { color: "var(--rd-red)" } : undefined}>
                        {data.httpStatus === null ? EMPTY_CELL : data.httpStatus}
                      </span>
                    ),
                  },
                  {
                    label: t(keys.integrationLogs.columnDuration),
                    value: <span className="rd-num">{formatDurationMs(data.durationMs, suffix)}</span>,
                  },
                  {
                    label: t(keys.integrationLogs.detailCorrelationId),
                    value: <span dir="ltr" style={mono}>{data.correlationId || EMPTY_CELL}</span>,
                  },
                ]}
              />
            </section>

            {data.error ? (
              <Alert tone="error" title={t(keys.integrationLogs.detailError)}>
                <span style={{ overflowWrap: "anywhere" }}>{data.error}</span>
              </Alert>
            ) : null}

            <PayloadBlock
              title={t(keys.integrationLogs.detailRequestPayload)}
              emptyLabel={t(keys.integrationLogs.detailPayloadEmpty)}
              payload={formatJsonPayload(data.requestPayload)}
            />
            <PayloadBlock
              title={t(keys.integrationLogs.detailResponsePayload)}
              emptyLabel={t(keys.integrationLogs.detailPayloadEmpty)}
              payload={formatJsonPayload(data.responsePayload)}
            />
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
